/**
 * Trade History Types - Updated to match VybeOrder C# schema
 */

// Enum mappings from C# schema
export enum VybeOrderType {
  Buy = 0,
  Sell = 1,
  BuyLimit = 2,
  SellLimit = 3,
  BuyStop = 4,
  SellStop = 5,
  BuyStopLimit = 6,
  SellStopLimit = 7,
  CloseBy = 8,
  Balance = 100,
  Credit = 101
}

export enum VybeDealType {
  DealBuy = 0,
  DealSell = 1,
  Balance = 2,
  Credit = 3,
  Charge = 4,
  Correction = 5,
  Bonus = 6,
  Commission = 7,
  DailyCommission = 8,
  MonthlyCommission = 9,
  DailyAgentCommission = 10,
  MonthlyAgentCommission = 11,
  InterestRate = 12,
  CanceledBuy = 13,
  CanceledSell = 14
}

export enum VybeOrderState {
  Started = 0,
  Placed = 1,
  Cancelled = 2,
  Partial = 3,
  Filled = 4,
  Rejected = 5,
  Expired = 6,
  RequestAdding = 7,
  RequestModifying = 8,
  RequestCancelling = 9
}
export enum VybePlacedType {
  Manually = 0,
  ByExpert = 1,
  ByDealer = 2,
  OnSL = 3,
  OnTP = 4,
  OnStopOut = 5,
  OnRollover = 6,
  OnVmargin = 8,
  Gateway = 9,
  Signal = 10,
  Settlement = 11,
  Transfer = 12,
  Sync = 13,
  ExternalService = 14,
  Migration = 15,
  Mobile = 16,
  Web = 17,
  OnSplit = 18,
  Default = 20
}

export enum VybeExpirationType {
  GTC = 0,
  Today = 1,
  Specified = 2,
  SpecifiedDay = 3
}

export enum VybeFillPolicy {
  FillOrKill = 0,
  ImmediateOrCancel = 1,
  FlashFill = 2,
  Any = 3
}

export enum VybeDirection {
  In = 0,
  Out = 1,
  InOut = 2,
  OutBy = 3
}
/** How this row was last merged into the client history store */
export type HistorySyncSource =
  | "realtime"
  | "server-reload"
  | "server-request";

/**
 * How to merge a server `VybeOnOrderHistory` batch into local state.
 * - `authoritative`: replace realtime-only rows in this batch’s bucket with server data (connect / full resync).
 * - `merge`: upsert rows from the batch without removing unrelated realtime rows (date-range requests).
 */
export type HistoryBatchMode = "authoritative" | "merge";

/** ISO timestamp when the row was last written from realtime or a server batch */
export type HistorySyncedAt = string;

export function formatHistorySyncSource(source: HistorySyncSource | undefined): string {
  if (!source) return "—";
  switch (source) {
    case "realtime":
      return "Realtime";
    case "server-reload":
      return "Server (reload)";
    case "server-request":
      return "Server (request)";
    default:
      return String(source);
  }
}

export function historySyncAbbrev(source: HistorySyncSource | undefined): string {
  if (!source) return "—";
  if (source === "realtime") return "RT";
  if (source === "server-reload") return "SR";
  if (source === "server-request") return "SQ";
  return "?";
}

// Nested object interfaces
export interface VybeDealInternal {
  ticketNumber: number;
  id: string;
  login: number;
  historyTime: number;
  orderTicket: number;
  openTimeAsDateTime: string;
  openTime: number;
  symbol: string;
  type: number; // VybeDealType
  direction: number; // VybeDirection
  openPrice: number;
  price: number;
  stopLoss: number;
  takeProfit: number;
  lots: number;
  volume: number;
  profit: number;
  profitRate: number;
  volumeRate: number;
  commission: number;
  fee: number;
  swap: number;
  expertId: number;
  positionTicket: number;
  comment: string;
  contractSize: number;
  digits: number;
  moneyDigits: number;
  freeProfit: number;
  trailRounder: number;
  openTimeMs: number;
  placedType: number; // VybePlacedType
  /** Client-only: last ingest path */
  syncSource?: HistorySyncSource;
  syncedAt?: HistorySyncedAt;
}

export interface VybeOrderInternal {
  ticketNumber: number;
  ticket: number;
  id: string;
  login: number;
  symbol: string;
  historyTime: number;
  openTime: number;
  expirationTime: number;
  executionTime: number;
  executionTimeAsDateTime: string;
  type: number; // VybeOrderType
  fillPolicy: number; // VybeFillPolicy
  expirationType: number; // VybeExpirationType
  placedType: number; // VybePlacedType
  openPrice: number;
  stopLimitPrice: number;
  price: number;
  stopLoss: number;
  takeProfit: number;
  lots: number;
  volume: number;
  requestLots: number;
  requestVolume: number;
  state: number; // VybeOrderState
  expertId: number;
  dealTicket: number;
  comment: string;
  contractSize: number;
  digits: number;
  baseDigits: number;
  profitRate: number;
  openTimeMs: number;
  openTimeMsAsDateTime: string;
  isAssociativeDealOrder: boolean;
  isTradeOrder: boolean;
  isLimitOrder: boolean;
  isStopOrder: boolean;
  isBuyOrder: boolean;
  isStopLimitOrder: boolean;
  /** Client-only: last ingest path */
  syncSource?: HistorySyncSource;
  syncedAt?: HistorySyncedAt;
}
// Main TradeOrder interface - matches VybeOrder C# schema exactly
export interface TradeOrder {
  // Core identification
  ticket: number;
  symbol: string;
  
  // Financial data
  profit: number;
  swap: number;
  commission: number;
  fee: number;
  
  // Close data
  closePrice: number;
  closeTime: string;
  closeLots: number;
  closeVolume: number;
  closeComment: string;
  
  // Open data  
  openPrice: number;
  openTime: string;
  lots: number;
  volume: number;
  contractSize: number;
  expertId: number;
  
  // Order classification (now numeric enums)
  placedType: number; // VybePlacedType
  orderType: number;  // VybeOrderType
  dealType: number;   // VybeDealType
  state: number;      // VybeOrderState
  
  // Stops and limits
  stopLoss: number;
  takeProfit: number;
  comment: string;
  
  // New fields from updated schema
  requestId: number;
  digits: number;
  profitRate: number;
  stopLimitPrice: number;
  expirationType: number; // VybeExpirationType
  expirationTime: string;
  fillPolicy: number;     // VybeFillPolicy
  openTimestampUTC: number;
  closeTimestampUTC: number;
  
  // Complex nested objects
  dealInternalIn: VybeDealInternal | null;
  dealInternalOut: VybeDealInternal | null;
  orderInternal: VybeOrderInternal | null;
  /** Client-only: last ingest path */
  syncSource?: HistorySyncSource;
  syncedAt?: HistorySyncedAt;
}
// Helper functions for enum display conversion
export const formatOrderType = (orderType: number): string => {
  const typeMap: Record<number, string> = {
    [VybeOrderType.Buy]: 'Buy',
    [VybeOrderType.Sell]: 'Sell',
    [VybeOrderType.BuyLimit]: 'Buy Limit',
    [VybeOrderType.SellLimit]: 'Sell Limit',
    [VybeOrderType.BuyStop]: 'Buy Stop',
    [VybeOrderType.SellStop]: 'Sell Stop',
    [VybeOrderType.BuyStopLimit]: 'Buy Stop Limit',
    [VybeOrderType.SellStopLimit]: 'Sell Stop Limit',
    [VybeOrderType.CloseBy]: 'Close By',
    [VybeOrderType.Balance]: 'Balance',
    [VybeOrderType.Credit]: 'Credit'
  };
  return typeMap[orderType] || `Unknown (${orderType})`;
};

export const formatDealType = (dealType: number): string => {
  const typeMap: Record<number, string> = {
    [VybeDealType.DealBuy]: 'Buy',
    [VybeDealType.DealSell]: 'Sell',
    [VybeDealType.Balance]: 'Balance',
    [VybeDealType.Credit]: 'Credit',
    [VybeDealType.Charge]: 'Charge',
    [VybeDealType.Correction]: 'Correction',
    [VybeDealType.Bonus]: 'Bonus',
    [VybeDealType.Commission]: 'Commission',
    [VybeDealType.DailyCommission]: 'Daily Commission',
    [VybeDealType.MonthlyCommission]: 'Monthly Commission',
    [VybeDealType.DailyAgentCommission]: 'Daily Agent Commission',
    [VybeDealType.MonthlyAgentCommission]: 'Monthly Agent Commission',
    [VybeDealType.InterestRate]: 'Interest Rate',
    [VybeDealType.CanceledBuy]: 'Canceled Buy',
    [VybeDealType.CanceledSell]: 'Canceled Sell'
  };
  return typeMap[dealType] || `Unknown (${dealType})`;
};

export const formatOrderState = (state: number): string => {
  const stateMap: Record<number, string> = {
    [VybeOrderState.Started]: 'Started',
    [VybeOrderState.Placed]: 'Placed',
    [VybeOrderState.Cancelled]: 'Cancelled',
    [VybeOrderState.Partial]: 'Partial',
    [VybeOrderState.Filled]: 'Filled',
    [VybeOrderState.Rejected]: 'Rejected',
    [VybeOrderState.Expired]: 'Expired',
    [VybeOrderState.RequestAdding]: 'Request Adding',
    [VybeOrderState.RequestModifying]: 'Request Modifying',
    [VybeOrderState.RequestCancelling]: 'Request Cancelling'
  };
  return stateMap[state] || `Unknown (${state})`;
};
export const formatPlacedType = (placedType: number): string => {
  const typeMap: Record<number, string> = {
    [VybePlacedType.Manually]: 'Manual',
    [VybePlacedType.ByExpert]: 'Expert',
    [VybePlacedType.ByDealer]: 'Dealer',
    [VybePlacedType.OnSL]: 'Stop Loss',
    [VybePlacedType.OnTP]: 'Take Profit',
    [VybePlacedType.OnStopOut]: 'Stop Out',
    [VybePlacedType.OnRollover]: 'Rollover',
    [VybePlacedType.OnVmargin]: 'V-Margin',
    [VybePlacedType.Gateway]: 'Gateway',
    [VybePlacedType.Signal]: 'Signal',
    [VybePlacedType.Settlement]: 'Settlement',
    [VybePlacedType.Transfer]: 'Transfer',
    [VybePlacedType.Sync]: 'Sync',
    [VybePlacedType.ExternalService]: 'External Service',
    [VybePlacedType.Migration]: 'Migration',
    [VybePlacedType.Mobile]: 'Mobile',
    [VybePlacedType.Web]: 'Web',
    [VybePlacedType.OnSplit]: 'Split',
    [VybePlacedType.Default]: 'Default'
  };
  return typeMap[placedType] || `Unknown (${placedType})`;
};

export const formatExpirationType = (expirationType: number): string => {
  const typeMap: Record<number, string> = {
    [VybeExpirationType.GTC]: 'Good Till Cancelled',
    [VybeExpirationType.Today]: 'Today',
    [VybeExpirationType.Specified]: 'Specified',
    [VybeExpirationType.SpecifiedDay]: 'Specified Day'
  };
  return typeMap[expirationType] || `Unknown (${expirationType})`;
};

export const formatFillPolicy = (fillPolicy: number): string => {
  const typeMap: Record<number, string> = {
    [VybeFillPolicy.FillOrKill]: 'Fill or Kill',
    [VybeFillPolicy.ImmediateOrCancel]: 'Immediate or Cancel',
    [VybeFillPolicy.FlashFill]: 'Flash Fill',
    [VybeFillPolicy.Any]: 'Any'
  };
  return typeMap[fillPolicy] || `Unknown (${fillPolicy})`;
};

export const formatDirection = (direction: number): string => {
  const typeMap: Record<number, string> = {
    [VybeDirection.In]: 'In',
    [VybeDirection.Out]: 'Out',
    [VybeDirection.InOut]: 'In/Out',
    [VybeDirection.OutBy]: 'Out By'
  };
  return typeMap[direction] || `Unknown (${direction})`;
};

/** Tailwind text class: buy-side order types use app profit blue, sell-side use loss red. */
export function orderTypeBuySellClass(orderType: number): string {
  switch (orderType) {
    case VybeOrderType.Buy:
    case VybeOrderType.BuyLimit:
    case VybeOrderType.BuyStop:
    case VybeOrderType.BuyStopLimit:
      return "text-blue-400";
    case VybeOrderType.Sell:
    case VybeOrderType.SellLimit:
    case VybeOrderType.SellStop:
    case VybeOrderType.SellStopLimit:
      return "text-red-400";
    default:
      return "text-foreground";
  }
}

/** Tailwind text class for deal buy/sell (and canceled buy/sell); other deal kinds stay neutral. */
export function dealTypeBuySellClass(dealType: number): string {
  if (
    dealType === VybeDealType.DealBuy ||
    dealType === VybeDealType.CanceledBuy
  ) {
    return "text-blue-400";
  }
  if (
    dealType === VybeDealType.DealSell ||
    dealType === VybeDealType.CanceledSell
  ) {
    return "text-red-400";
  }
  return "text-foreground";
}

/** Prefer server openTimeMs (usually ms); fall back to openTime (often seconds). */
export function dealOpenTimeMs(deal: VybeDealInternal): number {
  const ms = deal.openTimeMs;
  if (typeof ms === "number" && ms >= 1e12) return ms;
  if (typeof ms === "number" && ms > 0) return ms;
  const sec = deal.openTime;
  if (typeof sec === "number" && sec > 0 && sec < 1e11) return sec * 1000;
  return typeof sec === "number" ? sec : 0;
}

// Keep existing interfaces for backward compatibility
/** Matches server VybeOrderHistoryResponse.historySource */
export type OrderHistorySource = "Closed" | "Pending" | string | null;

export interface TradeHistoryBatch {
  success: boolean;
  errorMessage: string | null;
  orders: TradeOrder[];
  totalCount: number;
  action?: number | null;
  /** Optional: server distinguishes closed history vs pending internals */
  historySource?: OrderHistorySource;
  internals?: VybeOrderInternal[];
  deals?: VybeDealInternal[];
  /** Client-only: stamp applied to items from this batch */
  syncSource?: HistorySyncSource;
  /** Client-only: authoritative vs merge semantics */
  batchMode?: HistoryBatchMode;
}

export interface TradeHistoryRequest {
  From: string; // ISO date string
  To: string;   // ISO date string
  Sort: string;
  Ascending: boolean;
}

export interface TradeHistoryState {
  // High-performance lookups using Maps
  byTicket: Map<number, TradeOrder>;
  bySymbol: Map<string, TradeOrder[]>;
  
  // Loading states
  loadingStatus: 'idle' | 'loading' | 'complete' | 'error';
  errorMessage: string | null;
  
  // Metadata
  totalExpected: number;
  totalLoaded: number;
  
  // Date ranges for additional data fetching
  oldestTradeDate: string | null;
  newestTradeDate: string | null;
  
  // Computed arrays (expensive - compute only when needed)
  allTrades: TradeOrder[];
}

export interface TradeHistoryActions {
  processBatch: (batch: TradeHistoryBatch) => void;
  reset: () => void;
  
  // High-performance getters (O(1) Map lookups)
  getTradeByTicket: (ticket: number) => TradeOrder | undefined;
  getTradesBySymbol: (symbol: string) => TradeOrder[];
  
  // Data fetching for additional years
  requestMoreData: (fromDate: string, toDate: string) => void;
  
  // Status checks
  isComplete: () => boolean;
  isLoading: () => boolean;
}

export type TradeHistoryStore = TradeHistoryState & TradeHistoryActions;

// Filter types for UI
export type DateFilter = 'all' | 'today' | 'lastMonth' | 'lastYear';

export interface FilterOptions {
  dateFilter: DateFilter;
  symbolFilter?: string;
  profitFilter?: 'all' | 'profit' | 'loss';
}